import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Link,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import { FaDatabase } from 'react-icons/fa6';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { FiExternalLink } from 'react-icons/fi';
import { fetchBillDetail, fetchBillBySlug, generateAnalysis } from '../utils/utils';
import { getVerifiedVotesOnly } from '../utils/billVotePreferences';
import {
  fetchOpenStatesBillDetail,
  fetchNYBillDetail,
  getStateName,
  getStateBillHref,
  getStateBillSourceName,
  getDataSourceAttribution,
  buildStateBillPath,
} from '../utils/stateLegislationApi';
import { useUser } from '../context/UserContext';
import LikeDislikeButtons from '../components/LegislationComponents/LikeDislikeButtonsComponent/LikeDislikeButtons';
import CommentSection from '../components/LegislationComponents/CommentComponent/CommentSection';
import TakeActionModal from '../components/LegislationComponents/TakeActionComponent/TakeActionModal';
import AuthRequiredModal from '../components/AlertComponents/AuthRequiredModal';
import { shape } from '../theme/shape';

const BillDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: idFromUrl, stateCode, identifier } = useParams();
  const isStateBillRoute = !!stateCode && !!identifier;

  const paramValue = idFromUrl || location.state?.billId || null;
  const isSlug = paramValue && /^[a-zA-Z]+-\d+$/.test(paramValue);

  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, dnaUserId, userState } = useUser();
  const [activeTab, setActiveTab] = useState(0);
  const [showTakeActionModal, setShowTakeActionModal] = useState(false);
  const [analysisAuthModalOpen, setAnalysisAuthModalOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const analysisSectionRef = useRef(null);
  const [memoExpanded, setMemoExpanded] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });
  const [verifiedVotesOnly, setVerifiedVotesOnly] = useState(getVerifiedVotesOnly);
  const showToast = (message, severity = "error") => setToast({ open: true, message, severity });

  // Pick up changes made in the bills list filter (this tab) or another tab.
  useEffect(() => {
    const sync = () => setVerifiedVotesOnly(getVerifiedVotesOnly());
    window.addEventListener('focus', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('focus', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const isStateBill = isStateBillRoute || !!bill?.isStateBill;
  const canTakeAction = !isStateBill || (bill?.stateCode || bill?.state) === userState;

  // Fetch bill data
  useEffect(() => {
    const getBillDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        if (isStateBillRoute) {
          const passedBill = location.state?.bill;
          if (passedBill) {
            if (passedBill.useNYApi && passedBill.nyData) {
              const detail = await fetchNYBillDetail(passedBill.nyData);
              setBill(detail);
            } else if (passedBill.useOpenStates && passedBill.openStatesData) {
              const detail = await fetchOpenStatesBillDetail(passedBill.openStatesData, stateCode);
              setBill(detail);
            } else {
              setBill({ ...passedBill, stateCode, isStateBill: true });
            }
          } else {
            setError("State bill data not available. Please navigate from the bills list.");
          }
        } else if (paramValue) {
          const voteOpts = { verifiedOnly: verifiedVotesOnly };
          const data = isSlug
            ? await fetchBillBySlug(paramValue, voteOpts)
            : await fetchBillDetail(paramValue, voteOpts);
          setBill(data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    getBillDetails();
  }, [paramValue, isSlug, isStateBillRoute, stateCode, identifier, location.state?.bill, verifiedVotesOnly]);

  if (loading) return <Box textAlign="center" mt={4}><CircularProgress /></Box>;
  if (error) return <Typography color="error" align="center" mt={4}>Error: {error}</Typography>;
  if (!bill) return <Typography align="center" mt={4}>No details available for this bill.</Typography>;

  const billPathMap = {
    hr: "house-bill",
    hres: "house-resolution",
    hjres: "house-joint-resolution",
    s: "senate-bill",
    sres: "senate-resolution",
    sjres: "senate-joint-resolution",
  };

  const billHref = isStateBill
    ? getStateBillHref(bill)
    : `https://www.congress.gov/bill/${bill.congress}th-congress/${billPathMap[bill.type]}/${bill.bill_number}`;

  const billSourceName = isStateBill
    ? getStateBillSourceName(bill)
    : 'Congress.gov';

  const analysisBoxStyles = {
    '& strong': {
      display: 'block', fontSize: '1.25rem', fontWeight: 700,
      color: 'var(--primary-color, #047857)',
      marginTop: '2rem', marginBottom: '1rem', lineHeight: 1.4,
    },
    '& strong:first-of-type': { marginTop: 0 },
    '& ul': {
      paddingLeft: '1.5rem', marginBottom: '1.5rem', marginTop: '0.5rem', listStyle: 'none',
      '& li': {
        marginBottom: '0.75rem', lineHeight: 1.7, fontSize: '1rem',
        color: 'var(--text-color, #0b1f1a)', position: 'relative', paddingLeft: '1.25rem',
        '&::before': { content: '"•"', position: 'absolute', left: 0, color: 'var(--primary-color, #047857)', fontWeight: 700, fontSize: '1.2rem' },
      },
    },
    '& p': { fontSize: '1rem', marginTop: '0.5rem', marginBottom: '1rem', color: 'var(--text-color, #0b1f1a)', lineHeight: 1.7 },
  };

  const handleGenerateAnalysis = async () => {
    if (generating) return;
    if (!user) { setAnalysisAuthModalOpen(true); return; }
    setGenerating(true);
    try {
      await generateAnalysis(bill.id);
      const voteOpts = { verifiedOnly: verifiedVotesOnly };
      const refreshed = isSlug
        ? await fetchBillBySlug(paramValue, voteOpts)
        : await fetchBillDetail(paramValue, voteOpts);
      setBill(refreshed);
    } catch (e) {
      if (e.message && e.message.includes("one AI analysis per hour")) {
        showToast("You can only generate one AI analysis per hour. Please try again later.", "warning");
      } else {
        showToast(e.message || "Failed to generate analysis. Please try again later.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const shareUrl = isStateBill
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}${buildStateBillPath(bill)}`
    : `${typeof window !== 'undefined' ? window.location.origin : ''}/bills/${bill?.slug || bill?.id || ''}`;

  const handleShare = async () => {
    const urlToShare = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');
    const title = bill?.title ? `${(bill.type || '').toUpperCase()} ${bill.bill_number}: ${bill.title}` : 'Bill';
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, url: urlToShare });
      } catch (e) {
        if (e.name !== 'AbortError') {
          try {
            await navigator.clipboard?.writeText(urlToShare);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
          } catch {
            // fallback: copy failed
          }
        }
      }
    } else {
      try {
        await navigator.clipboard?.writeText(urlToShare);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch {
        // clipboard not available
      }
    }
  };

  const stripHtml = (html) => (html || '').replace(/<[^>]+>/g, '').trim();
  const memoText = stripHtml(bill.memo || '');
  const memoTruncated = memoText.length > 500 && !memoExpanded ? memoText.slice(0, 500) + '…' : memoText;

  const policyAreaLabel = isStateBill && bill.policy_area ? 'Law Section' : 'Policy Area';

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 3 }, minHeight: '100vh', bgcolor: '#f8fafc', overflowX: 'hidden' }}>
      {/* State badge at top */}
      {isStateBill && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            gap: 0.75,
            px: 2,
            py: 0.75,
            mb: 2,
            borderRadius: '8px',
            bgcolor: '#047857',
            maxWidth: '100%',
          }}
        >
          <AccountBalanceRoundedIcon sx={{ fontSize: 16, color: '#fff', flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              overflowWrap: 'anywhere',
            }}
          >
            {getStateName(bill.stateCode || bill.state || stateCode)} State Bill
          </Typography>
        </Box>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
        mb={2}
        onClick={e => e.stopPropagation()}
        sx={{ display: isStateBill ? 'none' : 'flex' }}
      >
        {/* Tabs: federal only; state bill has no tab bar */}
        {!isStateBill && (
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => { e.stopPropagation(); setActiveTab(newValue); }}
            textColor="inherit"
            variant="scrollable"
            scrollButtons={false}
            allowScrollButtonsMobile
            TabIndicatorProps={{ style: { backgroundColor: 'var(--primary-color)' } }}
            sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' } }}
          >
            <Tab
              label="Overview"
              sx={{ '&&': { color: activeTab === 0 ? '#047857' : '#0f172a', opacity: 1 }, minHeight: 44, fontWeight: activeTab === 0 ? 700 : 500, textTransform: 'none', fontSize: '0.95rem' }}
            />
            <Tab
              label="View Analysis"
              sx={{ '&&': { color: activeTab === 1 ? '#047857' : '#0f172a', opacity: 1 }, minHeight: 44, fontWeight: activeTab === 1 ? 700 : 500, textTransform: 'none', fontSize: '0.95rem' }}
            />
          </Tabs>
        )}
        {/* Like/Dislike — federal only */}
        {!isStateBill && <LikeDislikeButtons billId={bill.id} verifiedOnly={verifiedVotesOnly} />}
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* Overview tab (or the only view for state bills) */}
      {(activeTab === 0 || isStateBill) && (
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            mb={2}
            sx={{ color: '#000', lineHeight: 1.3, fontSize: { xs: '1.3rem', sm: '1.5rem' }, overflowWrap: 'break-word' }}
          >
            <span style={{ textTransform: 'uppercase' }}>{bill.type}</span>.{bill.bill_number} – {bill.title}
          </Typography>

          {/* Metadata: flat, no border or background */}
          <Box
            sx={{
              mb: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            {isStateBill && (
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#000', textTransform: 'uppercase', letterSpacing: 0.5 }}>Session</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 500, color: '#0f172a' }}>{bill.session}</Typography>
              </Box>
            )}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#000', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 500, color: '#0f172a' }}>{bill.status}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#000', textTransform: 'uppercase', letterSpacing: 0.5 }}>Sponsor</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 500, color: '#0f172a' }}>
                {bill.sponsor_name}
                {bill.sponsor_party ? ` (${bill.sponsor_party.charAt(0)})` : ''}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#000', textTransform: 'uppercase', letterSpacing: 0.5 }}>Cosponsors</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 500, color: '#0f172a' }}>
                {bill.cosponsors?.length ? bill.cosponsors.map(c => [`${(c.first_name || '').trim()}`, `${(c.last_name || '').trim()}`].join(' ').trim() || '—').join(', ') : 'None'}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#000', textTransform: 'uppercase', letterSpacing: 0.5 }}>{policyAreaLabel}</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 500, color: '#0f172a' }}>{bill.policy_area || 'N/A'}</Typography>
            </Box>
          </Box>

          {/* Summary — flat, no border or background */}
          <Box mt={2}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#0f172a', mb: 1 }}>Summary</Typography>
            <Typography sx={{ fontSize: 15, lineHeight: 1.6, color: '#000' }}>
              {bill.summary ? stripHtml(bill.summary) : 'No summary available.'}
            </Typography>
          </Box>

          {/* State-only: Sponsor's Memo with prominent Read more */}
          {isStateBill && memoText && (
            <Box mt={3} sx={{ p: 2.5, bgcolor: '#f1f5f9', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f172a', mb: 1.5 }}>Sponsor's Memo</Typography>
              <Typography sx={{ fontSize: 15, lineHeight: 1.7, color: '#000', whiteSpace: 'pre-wrap' }}>
                {memoTruncated}
              </Typography>
              {memoText.length > 500 && (
                <Button
                  onClick={() => setMemoExpanded(!memoExpanded)}
                  sx={{
                    mt: 1.5,
                    textTransform: 'none',
                    color: '#047857',
                    fontWeight: 700,
                    fontSize: 14,
                    px: 0,
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  {memoExpanded ? 'Show less' : 'Read more'}
                </Button>
              )}
            </Box>
          )}

          {/* "See easy-to-read analysis" callout — federal only */}
          {!isStateBill && (
            <Box
              sx={{
                mt: 3, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 1, cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { opacity: 0.8 },
              }}
              onClick={() => {
                setActiveTab(1);
                setTimeout(() => {
                  if (analysisSectionRef.current) analysisSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
              }}
            >
              <Typography
                variant="subtitle2" fontWeight={700}
                sx={{ color: 'var(--primary-color, #047857)', textDecoration: 'underline' }}
              >
                See easy-to-read analysis
              </Typography>
              <Box sx={{ color: 'var(--primary-color, #047857)', display: 'flex', alignItems: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Box>
            </Box>
          )}

          {/* Actions — flat, no card */}
          <Box sx={{ mt: 3, bgcolor: '#f8fafc' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
              {bill.xml_txt_url ? (
                <Button
                  variant="contained"
                  component="a"
                  href={bill.xml_txt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<FiExternalLink sx={{ color: '#fff' }} />}
                  sx={{
                    bgcolor: '#047857',
                    color: '#fff',
                    borderRadius: shape.radius.control,
                    minHeight: 36,
                    py: 0.75,
                    px: 1.5,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 13,
                    '&:hover': { bgcolor: '#065f46', color: '#fff' },
                  }}
                >
                  View Bill Text
                </Button>
              ) : (
                <Typography sx={{ fontSize: 14, color: '#334155', fontWeight: 500 }}>Bill text not available yet.</Typography>
              )}

              {canTakeAction && (
                <Button
                  variant="contained"
                  onClick={() => setShowTakeActionModal(true)}
                  sx={{
                    bgcolor: '#047857',
                    color: '#fff',
                    borderRadius: shape.radius.control,
                    minHeight: 36,
                    py: 0.75,
                    px: 1.5,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 13,
                    '&:hover': { bgcolor: '#065f46', color: '#fff' },
                  }}
                >
                  Take Action
                </Button>
              )}

              {isStateBill && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleShare}
                  startIcon={<ShareOutlinedIcon sx={{ color: '#0f172a', fontSize: 18 }} />}
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: shape.radius.control,
                    border: '1.5px solid #0f172a',
                    color: '#0f172a',
                    minHeight: 36,
                    py: 0.75,
                    px: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    '&:hover': {
                      bgcolor: '#f8fafc',
                      border: '1.5px solid #0f172a',
                      color: '#0f172a',
                    },
                  }}
                >
                  {shareCopied ? 'Copied!' : 'Share'}
                </Button>
              )}

              <Link
                href={billHref}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontWeight: 600,
                  fontSize: 14,
                  color: '#0f172a',
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                  '&:hover': { color: '#047857' },
                }}
              >
                View on {billSourceName} <FiExternalLink style={{ marginLeft: 6, color: 'inherit' }} />
              </Link>
            </Stack>
          </Box>

          {/* Comments — federal only */}
          {!isStateBill && (
            <Box mt={4}>
              <CommentSection billId={bill.id} dnaUserId={dnaUserId} />
            </Box>
          )}

          {/* Attribution — state only */}
          {isStateBill && getDataSourceAttribution(bill) && (
            <Box
              sx={{
                mt: 4,
                pt: 2,
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <FaDatabase style={{ fontSize: 18, color: '#000' }} />
              <Typography sx={{ fontSize: 13, color: '#000', fontWeight: 500 }}>
                State legislation data from {getDataSourceAttribution(bill)}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Analysis tab — federal only */}
      {activeTab === 1 && !isStateBill && (
        <Box ref={analysisSectionRef}>
          {(bill.ai_opinion || bill.xml_txt_url) && (
            <Box
              mb={3}
              sx={{ p: 2, backgroundColor: 'var(--primary-color, #047857)0F', borderRadius: 2, borderLeft: '3px solid var(--primary-color, #047857)' }}
            >
              <Typography variant="body2" sx={{ color: 'var(--text-color, #0b1f1a)', lineHeight: 1.6 }}>
                This is an AI-generated analysis that explains the bill in plain language, making it easier to understand than the technical legal text. We prompt the model to be nonpartisan, but cannot guarantee neutrality or complete accuracy. Please review the original bill text for details.
              </Typography>
            </Box>
          )}

          {!bill.ai_opinion && bill.xml_txt_url && (
            <Box sx={{ backgroundColor: '#f8fafc', borderRadius: 2, p: 3, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', mt: 2 }}>
              <Typography sx={{ color: '#334155', mb: 2, fontSize: '0.95rem' }}>
                No AI analysis yet—click below to generate one.
              </Typography>
              {!generating ? (
                <Button
                  variant="contained" onClick={handleGenerateAnalysis} disabled={generating}
                  sx={{ backgroundColor: 'var(--primary-color, #047857)', color: '#fff', textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, py: 1, '&:hover': { backgroundColor: '#065f46' } }}
                >
                  Generate Analysis
                </Button>
              ) : (
                <Box display="flex" alignItems="center" sx={{ backgroundColor: '#f1f5f9', px: 3, py: 1, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <CircularProgress size={16} sx={{ color: 'var(--primary-color, #047857)' }} />
                  <Typography ml={1.5} sx={{ color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>Generating…</Typography>
                </Box>
              )}
            </Box>
          )}

          {!bill.ai_opinion && !bill.xml_txt_url && (
            <Box sx={{ backgroundColor: '#f8fafc', borderRadius: 2, p: 3, border: '1px solid #e2e8f0', textAlign: 'center', mt: 2 }}>
              <Typography sx={{ color: '#0f172a', fontWeight: 600, mb: 1 }}>
                AI analysis isn't available for this bill yet.
              </Typography>
              <Typography variant="body2" sx={{ color: '#0f172a' }}>
                We can only generate an analysis when we have the full bill text in our system.
                This bill currently doesn't have a text link available.
              </Typography>
            </Box>
          )}

          {bill.ai_opinion && (
            <>
              <Divider sx={{ mb: 3, borderColor: 'var(--primary-color, #047857)' }} />
              <Box className="analysis-html" sx={analysisBoxStyles} dangerouslySetInnerHTML={{ __html: bill.ai_opinion }} />
            </>
          )}
        </Box>
      )}

      <TakeActionModal
        open={showTakeActionModal}
        onClose={() => setShowTakeActionModal(false)}
        billType={bill?.type}
        billNumber={bill?.bill_number}
        billTitle={bill?.title}
        billCongress={bill?.congress}
      />

      <AuthRequiredModal
        open={analysisAuthModalOpen}
        onClose={() => setAnalysisAuthModalOpen(false)}
        onLogin={() => {
          setAnalysisAuthModalOpen(false);
          navigate('/auth/signup');
        }}
        title="This feature is for logged-in users"
        description="Create a free account to generate AI bill analysis."
        confirmLabel="Sign up"
        dismissLabel="Keep browsing"
        footnote=""
      />
      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setToast(t => ({ ...t, open: false }))} severity={toast.severity} variant="filled">{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BillDetailPage;
